import registry from "unilib-registry/instance";
import { CurrencyCode } from "../../constants/enums";
import { FeatureFlag } from "../../constants/feature-flags";
import { FeatureFlagEntity } from "../../core/types";
import { TenantEntity } from "../../core/types/Tenant";
import { AnalyticsExchangeRate } from "../api/AnalyticsApiClient";

type AuthenticatedUserEntity = {
  tenant: TenantEntity & { featureFlags: FeatureFlagEntity[] };
};

export const BASE_EXCHANGE_RATE: AnalyticsExchangeRate = {
  baseCurrency: CurrencyCode.USD,
  quotes: [
    {
      quoteCurrency: CurrencyCode.USD,
      rate: 1.0,
      ingestionTime: new Date().toISOString(),
      quoteTime: new Date().toISOString(),
    },
  ],
};

// Use a function to get the current locale so it can be mocked in tests
const getCurrencyLocale = () => {
  return typeof window !== "undefined" ? navigator?.language : "en-US";
};

const allCurrencyCodes = Intl.supportedValuesOf("currency").filter(
  (c) => c in CurrencyCode
) as CurrencyCode[];

const extractCurrencyPart = (
  currencyDisplay: Intl.NumberFormatOptionsCurrencyDisplay,
  currency: CurrencyCode = CurrencyCode.USD,
  locale = getCurrencyLocale()
) => {
  const parts = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay,
  }).formatToParts(1);

  return parts.find((part) => part.type === "currency")?.value ?? "";
};

const symbolToCurrencyMap: Record<string, CurrencyCode[]> = {};

for (const code of allCurrencyCodes) {
  const symbol = extractCurrencyPart("narrowSymbol", code);
  if (!symbolToCurrencyMap[symbol]) {
    symbolToCurrencyMap[symbol] = [];
  }
  symbolToCurrencyMap[symbol].push(code);
}

// Filter to only symbols used by more than one currency
const conflictingSymbols: Record<string, CurrencyCode[]> = Object.fromEntries(
  Object.entries(symbolToCurrencyMap).filter(([_, list]) => list.length > 1)
);

function getSafeCurrencySymbol(
  currency: CurrencyCode,
  narrowSymbol: string,
  locale = getCurrencyLocale()
): string {
  if (currency === CurrencyCode.USD) {
    return "$";
  }

  const conflictList = conflictingSymbols[narrowSymbol];

  if (conflictList && conflictList.includes(currency)) {
    return extractCurrencyPart("symbol", currency, locale);
  }

  return narrowSymbol;
}

export const getCurrencyDisplayName = (
  currencyCode: CurrencyCode,
  options?: {
    symbolOnly: boolean;
  },
  locale = getCurrencyLocale()
) => {
  const name = extractCurrencyPart("name", currencyCode, locale);
  const code = extractCurrencyPart("code", currencyCode, locale);
  const narrowSymbol = extractCurrencyPart(
    "narrowSymbol",
    currencyCode,
    locale
  );

  if (!name || !code || !narrowSymbol) {
    return "-";
  }

  const symbol = getSafeCurrencySymbol(currencyCode, narrowSymbol, locale);

  if (options?.symbolOnly) {
    return `${code} (${symbol})`;
  }

  return `${name} - ${code} (${symbol})`;
};

function getCurrencyCode(currencyCode?: CurrencyCode): CurrencyCode {
  // Currently all tenants use USD as their base currency. We're implementing multi-currency
  // support incrementally, starting with specific areas like TRE (Ternary Reporting Engine).
  // Using tenant.preferredCurrencyCode globally would require updating all currency displays
  // across the application simultaneously, which is impractical for our phased approach.
  // We'll revisit this implementation as multi-currency support expands.

  try {
    if (typeof window !== "undefined") {
      const authenticatedUser =
        registry.get<AuthenticatedUserEntity>("authenticatedUser");

      const multiCurrencyEnabled = !!authenticatedUser.tenant.featureFlags.find(
        (featureFlag) => featureFlag.flag === FeatureFlag.MULTI_CURRENCY
      );

      // If the tenant doesn't have the multi-currency feature flag enabled, return the tenant's preferred currency code
      if (!multiCurrencyEnabled) {
        return (
          authenticatedUser.tenant.preferredCurrencyCode || CurrencyCode.USD
        );
      }
    }
    return currencyCode || CurrencyCode.USD;
  } catch (error) {
    console.error("Failed to get currency code:", error);
    return currencyCode || CurrencyCode.USD;
  }
}

function getExchangeRates() {
  // Currently all tenants use USD as their base currency. We're implementing multi-currency
  // support incrementally, starting with specific areas like TRE (Ternary Reporting Engine).
  // Using tenant.preferredCurrencyCode globally would require updating all currency displays
  // across the application simultaneously, which is impractical for our phased approach.
  // We'll revisit this implementation as multi-currency support expands.

  try {
    if (typeof window === "undefined") {
      return BASE_EXCHANGE_RATE;
    }
    return (
      registry.get<AnalyticsExchangeRate>("exchangeRates") ?? BASE_EXCHANGE_RATE
    );
  } catch (error) {
    console.error("Failed to get exchange rates:", error);
    return BASE_EXCHANGE_RATE;
  }
}

/*
 * Converts a value from the base currency to the target currency, defaults to USD.
 */
export function getExchangedValue(
  value: number,
  targetCurrency: CurrencyCode = CurrencyCode.USD
): number {
  const exchangeRates = getExchangeRates();
  const baseCurrency = exchangeRates.baseCurrency;

  if (baseCurrency === targetCurrency) {
    return value;
  }

  const exchangeRate = exchangeRates.quotes.find(
    (quote) => quote.quoteCurrency === targetCurrency
  )?.rate;

  if (exchangeRate) {
    return value * exchangeRate;
  }

  return value;
}

export const getExchangeRateDisplay = (
  baseCurrency: CurrencyCode,
  exchangeRate: number,
  quoteCurrency?: CurrencyCode
): string => {
  if (!quoteCurrency) {
    return "--";
  }

  return `${formatCurrencyRounded({
    currencyCode: baseCurrency,
    number: 1,
  })} = ${formatCurrencyRounded({
    currencyCode: quoteCurrency,
    number: exchangeRate,
    extended: exchangeRate !== 1,
  })}`;
};

export const formatNumberRounded = (num: number): string => {
  return new Intl.NumberFormat(getCurrencyLocale(), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

export const formatNumber = (num: number, round?: number): string => {
  return new Intl.NumberFormat(getCurrencyLocale(), {
    maximumFractionDigits: round ?? 3,
  }).format(num);
};

// TODO: Leverage currency conversion when multi currency support is implemented in the entire application
export const formatCurrency = (options: {
  accounting?: boolean;
  applyExchangeRate?: boolean;
  currencyCode?: CurrencyCode;
  number: number;
}): string => {
  const currencyCode = getCurrencyCode(options.currencyCode);

  let convertedNumber = options.applyExchangeRate
    ? getExchangedValue(options.number, currencyCode)
    : options.number;

  if (Math.abs(convertedNumber) < 0.001) {
    convertedNumber = 0;
  }

  const narrowSymbol = extractCurrencyPart("narrowSymbol", currencyCode);
  const symbol = getSafeCurrencySymbol(currencyCode, narrowSymbol);

  return new Intl.NumberFormat(getCurrencyLocale(), {
    style: "currency",
    currency: currencyCode,
    currencyDisplay: "narrowSymbol",
    ...(options.accounting ? { currencySign: "accounting" } : {}),
  })
    .format(convertedNumber)
    .replace(narrowSymbol, symbol);
};

// TODO: Leverage currency conversion when multi currency support is implemented in the entire application
export const formatCurrencyRounded = (options: {
  accounting?: boolean;
  applyExchangeRate?: boolean;
  currencyCode?: CurrencyCode;
  extended?: boolean;
  maxCharacters?: number;
  number: number;
}): string => {
  const currencyCode = getCurrencyCode(options.currencyCode);

  const convertedNumber = options.applyExchangeRate
    ? getExchangedValue(options.number, currencyCode)
    : options.number;

  let shouldApplyCompactNotation = Math.abs(convertedNumber) >= 10000000;

  const narrowSymbol = extractCurrencyPart("narrowSymbol", currencyCode);
  const symbol = getSafeCurrencySymbol(currencyCode, narrowSymbol);

  const formattedNumber = new Intl.NumberFormat(getCurrencyLocale(), {
    style: "currency",
    currency: currencyCode,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: options.extended ? 2 : 0,
    maximumFractionDigits: options.extended ? 6 : 0,
    ...(options.accounting ? { currencySign: "accounting" } : {}),
  })
    .format(convertedNumber)
    .replace(narrowSymbol, symbol);

  const numberOfCharacters = formattedNumber.length;

  if (options.maxCharacters) {
    shouldApplyCompactNotation = numberOfCharacters > options.maxCharacters;
  }

  if (shouldApplyCompactNotation) {
    return new Intl.NumberFormat(getCurrencyLocale(), {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 2,
      notation: "compact",
      compactDisplay: "long",
      ...(options.accounting ? { currencySign: "accounting" } : {}),
    })
      .format(convertedNumber)
      .replace(narrowSymbol, symbol);
  }

  return formattedNumber;
};

export const formatKilograms = (num: number, isAxis?: boolean) => {
  const kilogram = new Intl.NumberFormat(getCurrencyLocale(), {
    style: "unit",
    unit: "kilogram",
    minimumFractionDigits: isAxis ? 0 : 2,
    notation: "compact",
    compactDisplay: "long",
  }).format(num);

  if (isAxis) {
    return `${kilogram}`;
  }

  const superScript = String.fromCharCode(0x2082);
  return `${kilogram} CO${superScript}`;
};

export const formatPercentage = (
  num: number,
  maximumFractionDigits?: number
): string => {
  if (Number.isNaN(num)) {
    return "-- %";
  }

  if (!Number.isFinite(num)) {
    return "0 %";
  }

  return new Intl.NumberFormat(getCurrencyLocale(), {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: maximumFractionDigits ?? 1,
  }).format(num);
};

export const numericFromFormatted = (str: string): number => {
  return Number(str.split(",").join(""));
};
